import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { AlertTriangle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useBulkSetPayerMethod, type Payer } from '@/api/payers';

// Shown when an admin is about to clear the Office Ally connection. Any
// payer still set to Electronic would become un-submittable, so we flip
// them to Manual atomically before letting the settings save proceed.
// The bulk endpoint writes one audit row per payer flipped.
//
// In-flight claims are untouched: each claim already snapshotted its
// submission_method at creation, so a claim that was "Electronic, Ready"
// before this change stays that way in the list until it's acted on.

interface Props {
    payers: Payer[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Called after the bulk flip succeeds so the caller can proceed with the settings save. */
    onConfirmed: () => Promise<void> | void;
}

export function OADisableWarningDialog({ payers, open, onOpenChange, onConfirmed }: Props) {
    const mutation = useBulkSetPayerMethod();

    useEffect(() => {
        if (open) mutation.reset();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const electronic = payers.filter((p) => p.submissionMethod === 'Electronic');

    async function onConfirm() {
        try {
            if (electronic.length) {
                await mutation.mutateAsync({
                    ids: electronic.map((p) => p.id),
                    method: 'Manual',
                });
            }
            await onConfirmed();
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            const data = e.response?.data as { error?: string } | undefined;
            return data?.error ?? 'Failed to update payers';
        }
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[min(calc(100vw-2rem),36rem)]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-warning-text" aria-hidden />
                        Disable electronic submission?
                    </DialogTitle>
                    <DialogDescription>
                        Clearing Office Ally credentials will block 837P submissions. Any
                        payer still set to Electronic would become un-submittable, so we'll
                        flip the ones below to Manual before saving.
                    </DialogDescription>
                </DialogHeader>

                {serverError && <Alert variant="danger" className="mb-3">{serverError}</Alert>}

                {electronic.length === 0 ? (
                    <p className="text-sm text-text-muted">
                        No payers are on Electronic right now — you can safely clear the
                        connection.
                    </p>
                ) : (
                    <>
                        <p className="text-sm text-text-muted mb-2">
                            {electronic.length} {electronic.length === 1 ? 'payer is' : 'payers are'} currently
                            on Electronic and will be flipped to Manual:
                        </p>
                        <div className="max-h-60 overflow-y-auto border border-border rounded-md divide-y divide-border">
                            {electronic.map((p) => (
                                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                                    <span className="font-medium text-text">{p.name}</span>
                                    {p.payerId && (
                                        <span className="font-mono text-2xs text-text-subtle">
                                            {p.payerId}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-xs text-text-subtle">
                            Already-submitted claims keep their snapshot and are not affected.
                        </p>
                    </>
                )}

                <DialogFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="danger"
                        onClick={onConfirm}
                        disabled={mutation.isPending}
                    >
                        {mutation.isPending
                            ? 'Updating…'
                            : electronic.length === 0
                                ? 'Clear connection'
                                : `Flip ${electronic.length} to Manual & clear`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
